/**
 * The bracket preview of the IFMA draw (#465): the team-mate meetings and
 * the manual swap.
 *
 * The server sends the shape (`plan`, from `backend/src/lib/bracketSeeding.ts`)
 * and the athletes in draw-position order. The browser never builds a shape
 * of its own: a swap changes the athlete on a position, never the plan.
 *
 * The file has no imports, so a pure spec can load it
 * (`e2e/tests/bracket-draw-465.spec.ts`).
 */

/** One bout of the plan. A seed number is a draw position (1 to n). */
export interface DrawStep {
  stage: string;
  redSeed: number | null;
  blueSeed: number | null;
  redSourceIdx: number | null;
  blueSourceIdx: number | null;
}

/** Where two positions can first meet. */
export interface Meeting {
  /** The index of the plan step. */
  step: number;
  stage: string;
  /** True when that bout is the first contest of one of the two. */
  firstContest: boolean;
}

/** The index of the step in which each winner competes next. */
function parents(plan: readonly DrawStep[]): Map<number, number> {
  const up = new Map<number, number>();
  plan.forEach((s, i) => {
    if (s.redSourceIdx !== null) up.set(s.redSourceIdx, i);
    if (s.blueSourceIdx !== null) up.set(s.blueSourceIdx, i);
  });
  return up;
}

/** The steps of position `pos`, from its first contest to the Final. */
function pathOf(plan: readonly DrawStep[], up: Map<number, number>, pos: number): number[] {
  const path: number[] = [];
  let at: number | undefined = plan.findIndex(s => s.redSeed === pos || s.blueSeed === pos);
  while (at !== undefined && at >= 0) { path.push(at); at = up.get(at); }
  return path;
}

/** The bout in which positions `a` and `b` can first meet. */
export function meeting(plan: readonly DrawStep[], a: number, b: number): Meeting | null {
  const up = parents(plan);
  const pathA = pathOf(plan, up, a);
  const pathB = pathOf(plan, up, b);
  const onB = new Set(pathB);
  const step = pathA.find(s => onB.has(s));
  if (step === undefined) return null;
  return { step, stage: plan[step].stage, firstContest: step === pathA[0] || step === pathB[0] };
}

/** A pair of team mates and the bout in which they can first meet. */
export interface TeamMeeting extends Meeting {
  /** The two draw positions, 1 to n, the lower first. */
  a: number;
  b: number;
  team: string;
}

/**
 * Each pair of team mates in the seats (index 0 is position 1), with the bout
 * in which they can first meet. The pairs that can meet in the first contest
 * of one of them come first, then the earlier meetings.
 */
export function teamMeetings(
  plan: readonly DrawStep[],
  seats: ReadonlyArray<{ team_name?: string | null }>,
): TeamMeeting[] {
  const out: TeamMeeting[] = [];
  for (let i = 0; i < seats.length; i++) {
    const team = seats[i].team_name;
    if (!team) continue;
    for (let j = i + 1; j < seats.length; j++) {
      if (seats[j].team_name !== team) continue;
      const m = meeting(plan, i + 1, j + 1);
      if (m) out.push({ ...m, a: i + 1, b: j + 1, team });
    }
  }
  return out.sort((x, y) => Number(y.firstContest) - Number(x.firstContest) || x.step - y.step || x.a - y.a);
}

/** The seats with the athletes on positions `a` and `b` (1 to n) swapped. */
export function swapSeats<T>(seats: readonly T[], a: number, b: number): T[] {
  const next = [...seats];
  [next[a - 1], next[b - 1]] = [next[b - 1], next[a - 1]];
  return next;
}
